import { useContext, useEffect, useRef, useState } from 'react';
import {
  LoginSignupForm,
  VerificationView,
  LoginAdapterContext,
} from '@ow/shared/login';
import { useTranslation } from '../../hooks/use-translation';
import GlobalContext from '../../context/global-context';
import { useWebsiteLoginAdapter } from './hooks/use-website-login-adapter';
import {
  kPostLoginAppKey,
  kPostLoginOverrideUrlKey,
  kPostLoginReviewKey,
  kSawAnimationKey,
} from './constants';
import useBus from '../../hooks/use-bus';
import { AppBusEventType } from '../../interfaces/bus-events';
import { useUserProfile } from '../../context/user-profile-context';
import { useAnalytics } from '../../hooks/use-analytics';
import {
  useDownloadClickHandler,
  DownloadClickComponent,
} from '../../hooks/use-download-click-handler';
import { OwAppTile } from '../../interfaces/domain/ow-app-tile.modal';
import { OwApp } from '../../interfaces/domain/ow-app.modal';
import { SessionStorage } from '@ow/shared/utils/storage/session-storage';
import { LoginCloseType } from './login';
// -----------------------------------------------------------------------------
enum View {
  LoginSignup,
  Verification,
}

// -----------------------------------------------------------------------------
// The public-web login flow: an email/password + social form rendered in a modal.
export const WebLogin = (): JSX.Element => {
  // ---------------------------------------------------------------------------
  const [view, setView] = useState<View>(View.LoginSignup);
  const adapter = useWebsiteLoginAdapter();
  const { t } = useTranslation();
  const { config } = useContext(GlobalContext);
  const footerLinks = config?.publicRuntimeConfig?.links?.footerLinks;
  const { isLoggedIn, currentUser } = useUserProfile();
  const [prevLoggedIn, setPrevLoggedIn] = useState(isLoggedIn);
  const [hideLogin, setHideLogin] = useState(false);
  const [showLogin, setShowLogin] = useState<boolean>(false);
  const waitingForLogin = useRef<boolean>(false);
  const [postLoginRedirect, setPostLoginRedirect] = useState<string>(null);
  const [postLoginDownloadApp, setPostLoginDownloadApp] = useState<
    OwApp | OwAppTile
  >(null);

  const [triedLogin, setTriedLogin] = useState(false);
  const [showGuestDownloadSkipLogin, setShowGuestDownloadSkipLogin] =
    useState<boolean>(true);
  const analytics = useAnalytics();
  const { handleClickDownload } = useDownloadClickHandler({
    component: DownloadClickComponent.Login,
    item: null,
  });

  useEffect(() => {
    setHideLogin(prevLoggedIn === false && prevLoggedIn !== isLoggedIn);
    setPrevLoggedIn(isLoggedIn);
  }, [isLoggedIn]);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    if (currentUser.profile.unverified) {
      setView(View.Verification);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    removeSessionStorageDownloadKeys();
  }, [isLoggedIn]);

  useBus(AppBusEventType.App.ShowLoginModal, (event) => {
    analytics.track('appstore_login_view', {
      source: event?.data?.source || 'unknown',
    });

    const hideSkipOptions = ['header', 'userReview'];
    const shouldShowSkipOption = !hideSkipOptions.includes(event?.data?.source);
    setShowGuestDownloadSkipLogin(shouldShowSkipOption);

    if (event.data.app) {
      SessionStorage.set(kPostLoginAppKey, JSON.stringify(event.data.app));
      setPostLoginDownloadApp(event.data.app);
    }

    if (event?.data?.source === 'userReview') {
      SessionStorage.setBool(kPostLoginReviewKey, true);
    }

    if (triedLogin) {
      return;
    }

    setPostLoginRedirect(event.data.overrideUrl || null);
    SessionStorage.set(kPostLoginOverrideUrlKey, event.data.overrideUrl);
    setShowLogin(event.data.show);
  });

  // ---------------------------------------------------------------------------
  const handleShowVerification = (isWaiting = false) => {
    waitingForLogin.current = waitingForLogin.current || isWaiting;
    setView(View.Verification);
    analytics.track('appstore_verification_view', {
      network: 'overwolf',
    });
  };

  // ---------------------------------------------------------------------------
  const handleShowDownload = (overrideUrl: string, app: OwApp | OwAppTile) => {
    setTimeout(async () => {
      await handleClickDownload(null, overrideUrl, true, app);
    }, 500);
  };

  // ---------------------------------------------------------------------------
  const removeSessionStorageDownloadKeys = () => {
    const overrideUrl = SessionStorage.get(kPostLoginOverrideUrlKey).raw;
    SessionStorage.remove(kPostLoginOverrideUrlKey);
    const app = SessionStorage.get(kPostLoginAppKey).asObject() as
      | OwApp
      | OwAppTile;

    SessionStorage.remove(kPostLoginAppKey);

    if (overrideUrl && app) {
      handleShowDownload(overrideUrl, app);
    }
  };

  // ---------------------------------------------------------------------------
  const handleClose = (loginCloseType: LoginCloseType = null) => {
    SessionStorage.remove(kSawAnimationKey);
    setShowLogin(false);
    setTriedLogin(true);

    if (loginCloseType === 'skip') {
      removeSessionStorageDownloadKeys();
    } else if (loginCloseType && postLoginRedirect) {
      handleShowDownload(postLoginRedirect, postLoginDownloadApp);
    }

    if (loginCloseType) {
      analytics.track(`appstore_login_${loginCloseType}`, {
        network: 'overwolf',
      });
    }
  };

  if (!showLogin) {
    return <></>;
  }

  // ---------------------------------------------------------------------------
  return (
    <LoginAdapterContext.Provider value={adapter}>
      <div
        id="login-overlay"
        // onClick={() => handleClose()}
        className="login-overlay"
      >
        <section
          className="modal-page"
          style={{ opacity: 1 }}
          onClick={(e) => e.stopPropagation()}
        >
          <section className="in-app-window" style={{ opacity: 1 }}>
            <LoginSignupForm
              show={view === View.LoginSignup}
              handleShowVerification={handleShowVerification}
              handleClose={handleClose}
              hideLogin={hideLogin}
              showGuestDownloadSkipLogin={showGuestDownloadSkipLogin}
              t={t}
              track={analytics.track}
              termsUrl={footerLinks?.terms ?? ''}
              privacyUrl={footerLinks?.privacy ?? ''}
            />
            <VerificationView
              show={view === View.Verification}
              handleClose={handleClose}
              source="login"
            />
          </section>
        </section>
      </div>
    </LoginAdapterContext.Provider>
  );
};

export default WebLogin;
